import type { AdminReview, AdminTransactionHistory } from "./types";
import { req, privateDocumentBlob } from "./transport";
import type { AdminPortfolioMedia, AdminListingSummary, AdminCustomersResponse, AdminCustomerBusinessLicense, AdminCustomerRecordKind, AdminCustomerRecordsPage } from "./types";

export const administrationApi = {
  async adminUpdateUser(id: number, patch: { firstName?: string; lastName?: string; email?: string }) {
    return req<{ ok: boolean; user: any }>(`/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
  },

  async adminResetPassword(id: number, password: string) {
    return req<{ ok: boolean }>(`/admin/users/${id}/password`, { method: "POST", body: JSON.stringify({ password }) });
  },

  async adminClearIdentityReview(id: number) {
    return req<{ ok: boolean }>(`/admin/users/${id}/clear-identity-review`, { method: "POST" });
  },

  // Increment 42: background jobs
  async adminJobs() {
    const r = await req<{ jobs: any[] }>("/admin/jobs");
    return r?.jobs ?? [];
  },

  async adminJobRuns(key?: string) {
    const r = await req<{ runs: any[] }>(`/admin/jobs/runs${key ? `?key=${encodeURIComponent(key)}` : ""}`);
    return r?.runs ?? [];
  },

  async adminRunJob(key: string) {
    return req<{ ok?: boolean; key?: string; summary?: any; error?: string; skipped?: boolean }>(`/admin/jobs/${encodeURIComponent(key)}/run`, { method: "POST" });
  },

  // v3.9.54 (Stage 2 / B3) — admin & moderation audit trail
  async adminAudit(params?: { limit?: number; offset?: number; action?: string }) {
    const sp = new URLSearchParams();
    if (params?.limit) sp.set("limit", String(params.limit));
    if (params?.offset) sp.set("offset", String(params.offset));
    if (params?.action) sp.set("action", params.action);
    const qs = sp.toString();
    return req<{ entries: any[]; total: number; limit: number; offset: number }>(`/admin/audit${qs ? `?${qs}` : ""}`);
  },

  // Increment 15: admin console
  async adminStats() {
    return req<any>("/admin/stats");
  },

  async adminStorageHealth() {
    return req<{ driver: "gcs" | "local"; bucket: string | null; ok: boolean; persistent: boolean; detail: string }>("/admin/storage-health");
  },

  // v3.9.21 — rich Admin Portal API (ported from UAT)
  async adminAdmins() {
    const r = await req<{ admins: any[] }>("/admin/admins");
    return r?.admins ?? [];
  },

  async adminClients(q?: string, status?: string) {
    const p = new URLSearchParams(); if (q) p.set("q", q); if (status) p.set("status", status);
    return req<{ stats: any; rows: any[] }>(`/admin/clients?${p.toString()}`);
  },

  async adminCustomers(opts: { q?: string; status?: string; type?: string; kyc?: string; professional?: string } = {}) {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v) p.set(k, v as string);
    return req<AdminCustomersResponse>(`/admin/customers?${p.toString()}`);
  },

  async adminDashboard() {
    return req<any>("/admin/dashboard");
  },

  async adminEmailOutbox() {
    return req<any>("/admin/email-outbox");
  },

  async adminMedia(status = "all") {
    const r = await req<{ media: AdminPortfolioMedia[] }>(`/admin/media?status=${encodeURIComponent(status)}`, {}, { rejectUnauthorized: true });
    return r?.media ?? [];
  },

  async adminMemberDetail(id: number) {
    return req<any>(`/admin/members/${id}/detail`);
  },

  adminCustomerRecords(id: number, kind: AdminCustomerRecordKind, offset = 0) {
    return req<AdminCustomerRecordsPage>(`/admin/members/${id}/records?kind=${kind}&limit=50&offset=${offset}`);
  },

  adminCustomerBusinessLicenses(id: number, companyId: string, offset = 0) {
    return req<{ licenses: AdminCustomerBusinessLicense[]; total: number; limit: number; offset: number }>(`/admin/members/${id}/businesses/${encodeURIComponent(companyId)}/licenses?limit=50&offset=${offset}`);
  },

  async adminCustomerDocumentBlob(path: string) {
    return privateDocumentBlob(path);
  },

  async adminModerateMedia(id: string, decision: "approve" | "reject", reviewVersion: string, note?: string) {
    return req<{ ok: boolean; status: string }>(`/admin/media/${encodeURIComponent(id)}/${decision}`, { method: "POST", body: JSON.stringify({ note, reviewVersion }) }, { rejectUnauthorized: true });
  },

  async adminRetryEmails() {
    return req<{ ok: boolean; retried: number; sent: number }>("/admin/jobs/email-retry", { method: "POST" });
  },

  async adminSetAdminRole(id: number, make: boolean) {
    return req<{ ok: boolean }>(`/admin/users/${id}/admin-role`, { method: "POST", body: JSON.stringify({ make }) });
  },

  async adminWorkers(q?: string, status?: string, kyc?: string) {
    const p = new URLSearchParams(); if (q) p.set("q", q); if (status) p.set("status", status); if (kyc) p.set("kyc", kyc);
    return req<{ stats: any; rows: any[] }>(`/admin/workers?${p.toString()}`);
  },

  async adminUsers(q?: string) {
    const r = await req<{ users: any[] }>(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    return r?.users ?? [];
  },

  async adminSuspend(id: number) {
    return req<{ ok: boolean; accountStatus: string }>(`/admin/users/${id}/suspend`, { method: "POST" });
  },

  async adminReactivate(id: number) {
    return req<{ ok: boolean; accountStatus: string }>(`/admin/users/${id}/reactivate`, { method: "POST" });
  },

  async adminListings(type: string, q?: string) {
    const r = await req<{ type: string; listings: AdminListingSummary[] }>(`/admin/listings?type=${encodeURIComponent(type)}${q ? `&q=${encodeURIComponent(q)}` : ""}`);
    return r?.listings ?? [];
  },

  async adminModerate(type: string, id: string, action: "hide" | "restore") {
    return req<{ ok: boolean; hidden: boolean }>(`/admin/listings/${encodeURIComponent(type)}/${encodeURIComponent(id)}/${action}`, { method: "POST" });
  },

  async adminReviews(q?: string) {
    const r = await req<{ reviews: AdminReview[] }>(`/admin/reviews${q ? `?q=${encodeURIComponent(q)}` : ""}`, {}, { rejectUnauthorized: true });
    return r?.reviews ?? [];
  },

  async adminDeleteReview(id: string) {
    return req<{ ok: boolean }>(`/admin/reviews/${encodeURIComponent(id)}`, { method: "DELETE" }, { rejectUnauthorized: true });
  },

  async adminTransactions() {
    return req<AdminTransactionHistory>("/admin/transactions");
  }
};
